import React from 'react';
import './ClientProfilesOverlay.css';

function ClientProfilesOverlay({ t, clientProfilesOverlay }) {
  const {
    setExpandedSection,
    clientProfilesLoading,
    clientProfilesError,
    clientProfilesExtended,
    activeClientProfile,
    activeClientDisplayIndex,
    handleClientProfileTabSelect,
    clientProfileVisible,
    personaList,
    getMetaKeyForPersona,
    highlightedMetaKey,
    setHighlightedMetaKey,
    metadataEntries,
    motivationList,
    barrierList,
    nextSteps,
    transcriptLines,
    handleClientProfileReveal,
  } = clientProfilesOverlay;

  return (
    <>
      <h2 id="overlay-heading" className="client-title">{t('clients_title')}</h2>
      <div className="client-overlay-content">
        <p>
          {t('clients_p1')}
        </p>
        <p>
          {t('clients_p2')}
        </p>

        <div className="client-profiles-explorer">
          <div className="client-profiles-header">
            <h3>{t('clients_roster_heading')}</h3>
            <p>
              {t('clients_roster_p')}
            </p>
            <p className="about-citation">
              {t('clients_citation')}
              {' '}
              <a href="https://github.com/cimhasgithub/MIBOT_ACL2025" target="_blank" rel="noopener noreferrer">
                https://github.com/cimhasgithub/MIBOT_ACL2025
              </a>,
              which also includes participant surveys and AutoMISC annotations.
            </p>
          </div>
          {clientProfilesLoading && (
            <div className="client-profiles-status">
              {t('clients_loading')}
            </div>
          )}
          {clientProfilesError && !clientProfilesLoading && (
            <div className="client-profiles-status error">
              {clientProfilesError}
            </div>
          )}
          {!clientProfilesLoading && !clientProfilesError && clientProfilesExtended.length > 0 && (
            <div className="client-profiles-browser">
              <div className="client-profile-tabs" role="tablist">
                {clientProfilesExtended.map((profile, idx) => (
                  <button
                    key={profile.profile_id ?? `profile-${idx}`}
                    type="button"
                    role="tab"
                    className={`client-profile-tab${activeClientProfile?.profile_id === profile.profile_id ? ' active' : ''}`}
                    aria-selected={activeClientProfile?.profile_id === profile.profile_id}
                    onClick={() => handleClientProfileTabSelect(profile, idx)}
                  >
                    <span className="client-profile-label">{t('clients_tab_label', { number: idx + 1 })}</span>
                  </button>
                ))}
              </div>
              {activeClientProfile && (
                <div
                  className={`client-profile-detail${clientProfileVisible ? '' : ' obscured'}`}
                  role="tabpanel"
                >
                  <div
                    className="client-profile-content"
                    aria-hidden={!clientProfileVisible}
                  >
                    <h3 className="client-profile-number">
                      {t('clients_profile_heading', { number: activeClientDisplayIndex + 1 })}
                    </h3>
                    <p className="client-profile-meta-note">
                      {t('clients_meta_note')}
                    </p>
                    <div className="client-profile-grid">
                      <div className="profile-grid-row">
                        <div className="profile-section">
                          <div className="section-header-with-popout">
                            <h4>{t('clients_persona_heading')}</h4>
                            <button
                              type="button"
                              className="section-popout-btn"
                              onClick={() => setExpandedSection('persona')}
                              title={t('clients_section_expand_title')}
                              aria-label={t('clients_persona_expand_aria')}
                            >
                              ⤢
                            </button>
                          </div>
                          {personaList.length > 0 ? (
                            <ol className="profile-list compact">
                              {personaList.map((item, idx) => {
                                const metaKey = getMetaKeyForPersona(item);
                                return (
                                  <li
                                    key={`persona-${idx}`}
                                    data-meta-key={metaKey}
                                    className={`persona-meta${highlightedMetaKey && highlightedMetaKey === metaKey ? ` hl hl-${metaKey}` : ''}`}
                                    onMouseEnter={() => metaKey && setHighlightedMetaKey(metaKey)}
                                    onMouseLeave={() => setHighlightedMetaKey(null)}
                                  >
                                    {item}
                                  </li>
                                );
                              })}
                            </ol>
                          ) : (
                            <p className="profile-empty">{t('clients_persona_empty')}</p>
                          )}
                        </div>

                        <div className="profile-section metadata-section">
                          <div className="section-header-with-popout">
                            <h4>{t('clients_metadata_heading')}</h4>
                            <button
                              type="button"
                              className="section-popout-btn"
                              onClick={() => setExpandedSection('metadata')}
                              title={t('clients_section_expand_title')}
                              aria-label={t('clients_metadata_expand_aria')}
                            >
                              ⤢
                            </button>
                          </div>
                          <p className="section-subtitle">{t('clients_metadata_subtitle')}</p>
                          {metadataEntries.length > 0 ? (
                            <dl className="client-metadata-list">
                              {metadataEntries.map((item) => (
                                <div
                                  className={`metadata-row meta-row${highlightedMetaKey && highlightedMetaKey === item.key ? ` hl hl-${item.key}` : ''}`}
                                  key={item.key}
                                  data-meta-key={item.key}
                                  onMouseEnter={() => setHighlightedMetaKey(item.key)}
                                  onMouseLeave={() => setHighlightedMetaKey(null)}
                                >
                                  <dt>{item.label}</dt>
                                  <dd>{item.value}</dd>
                                </div>
                              ))}
                            </dl>
                          ) : (
                            <p className="profile-empty">{t('clients_metadata_empty')}</p>
                          )}
                        </div>
                      </div>

                      <div className="profile-grid-row">
                        <div className="profile-section">
                          <div className="section-header-with-popout">
                            <h4>{t('clients_motivations_heading')}</h4>
                            <button
                              type="button"
                              className="section-popout-btn"
                              onClick={() => setExpandedSection('motivations')}
                              title={t('clients_section_expand_title')}
                              aria-label={t('clients_motivations_expand_aria')}
                            >
                              ⤢
                            </button>
                          </div>
                          {motivationList.length > 0 ? (
                            <div className="profile-flat-list compact">
                              {motivationList.map((item, idx) => (
                                <div className="profile-flat-item" key={`motivation-${idx}`}>
                                  {item.category && (
                                    <span
                                      className={`profile-tag ${item.category
                                        .toString()
                                        .toLowerCase()
                                        .replace(/[^a-z0-9+]/g, '')}`}
                                    >
                                      {item.category}
                                    </span>
                                  )}
                                  <span>{item.text}</span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="profile-empty">{t('clients_motivations_empty')}</p>
                          )}
                        </div>

                        <div className="profile-section">
                          <div className="section-header-with-popout">
                            <h4>{t('clients_barriers_heading')}</h4>
                            <button
                              type="button"
                              className="section-popout-btn"
                              onClick={() => setExpandedSection('barriers')}
                              title={t('clients_section_expand_title')}
                              aria-label={t('clients_barriers_expand_aria')}
                            >
                              ⤢
                            </button>
                          </div>
                          {barrierList.length > 0 ? (
                            <div className="profile-flat-list compact">
                              {barrierList.map((item, idx) => (
                                <div className="profile-flat-item" key={`barrier-${idx}`}>
                                  {item.category && (
                                    <span
                                      className={`profile-tag muted ${item.category
                                        .toString()
                                        .toLowerCase()
                                        .replace(/[^a-z0-9+]/g, '')}`}
                                    >
                                      {item.category}
                                    </span>
                                  )}
                                  <span>{item.text}</span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="profile-empty">{t('clients_barriers_empty')}</p>
                          )}
                        </div>
                      </div>

                      <div className="profile-grid-row full-width">
                        <div className="profile-section">
                          <div className="section-header-with-popout">
                            <h4>{t('clients_nextsteps_heading')}</h4>
                            <button
                              type="button"
                              className="section-popout-btn"
                              onClick={() => setExpandedSection('nextsteps')}
                              title={t('clients_section_expand_title')}
                              aria-label={t('clients_nextsteps_expand_aria')}
                            >
                              ⤢
                            </button>
                          </div>
                          {nextSteps.length > 0 ? (
                            <ul className="profile-list numbered compact">
                              {nextSteps.map((step, idx) => (
                                <li key={`next-step-${idx}`}>{step}</li>
                              ))}
                            </ul>
                          ) : (
                            <p className="profile-empty">{t('clients_nextsteps_empty')}</p>
                          )}
                        </div>
                      </div>

                      <div className="profile-grid-row full-width">
                        <div className="profile-section transcript-section">
                          <div className="transcript-header">
                            <div>
                              <h4>{t('clients_transcript_heading')}</h4>
                              <p className="section-subtitle">
                                {t('clients_transcript_subtitle')}
                                <span className="transcript-length">
                                  {t('clients_transcript_volleys', { count: transcriptLines.length })}
                                </span>
                              </p>
                            </div>
                            <button
                              type="button"
                              className="section-popout-btn"
                              onClick={() => setExpandedSection('transcript')}
                              title={t('clients_section_expand_title')}
                              aria-label={t('clients_transcript_expand_aria')}
                            >
                              ⤢
                            </button>
                          </div>
                          <div className="client-transcript-scroll">
                            {transcriptLines.length > 0 ? (
                              transcriptLines.map((line, idx) => (
                                <div
                                  key={`${activeClientProfile.profile_index || 'profile'}-${idx}`}
                                  className={`transcript-line transcript-${line.speaker}`}
                                >
                                  <strong className="speaker-label">
                                    {line.speaker === 'counsellor' ? t('clients_transcript_counsellor') : t('clients_transcript_client')}
                                  </strong>
                                  <p>{line.vol_text}</p>
                                </div>
                              ))
                            ) : (
                              <p className="profile-empty">{t('clients_transcript_empty')}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  {!clientProfileVisible && (
                    <div className="client-profile-mask">
                      <div className="client-profile-mask-content">
                        <h3 className="client-profile-mask-title">
                          {t('clients_profile_heading', { number: activeClientDisplayIndex + 1 })}
                        </h3>
                        <p className="client-profile-mask-subtitle">
                          {t('clients_mask_subtitle')}
                        </p>
                        <button
                          type="button"
                          className="client-profile-reveal-btn"
                          onClick={handleClientProfileReveal}
                        >
                          {t('clients_mask_view_btn')}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default ClientProfilesOverlay;
